import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, OutlinedInput, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography, CircularProgress} from "@mui/material";
import {SectionTitle} from "../../../export/advanced_export";
import EnhancedSelect from "../../../../../../components/EnhancedSelect";
import {MappingArrow, SearcherIcon} from "../../../../../../utils/icons";
import {MappingHeader, MappingCell} from "../advanced_supplier_import";
import {Ballot, Clear} from "@mui/icons-material";
import CatalogCheckbox from "../../../../../../components/EnhancedCheckbox";
import NavigationButtons from "./NavigationButtons";
import ErrorIcon from "@mui/icons-material/Error";
import cloneDeep from 'lodash/cloneDeep';
import {Notifier} from "../../../../../documents/document_components";
import {SUPPLIER_SYNDICATION_ENABLED, useDebouncedEffect, AI_MAPPING_ENABLED} from "../../../../../../utils/utils";
import AttributeMapper from "./components/AttributeMapper";
import Fuse from "fuse.js";
import ColumnRuleEditor from "./components/ColumnRuleEditor";
import MappingReviewDialog from "../../../../components/Dialogs/MappingReviewDialog";
import {aiMapAttributes} from "../../../../services/ai_service";
import EnhancedSnackbar from "../../../../../../components/EnhancedSnackbar";
import { tableRowSx, inputHeight, pageContentGap } from "../../../../../../theme";
import type { WizardSharedProps, RetailerAttribute, ParsedField } from "../types";
import type { AttributeMappingRule } from "../../../../types";

// region TypeScript Interfaces
type AttributeMappingProps = WizardSharedProps & {
    fileParsedFields: ParsedField[];
};

interface ConditionalCheckboxProps {
    validationRules?: RetailerAttribute["ValidationRules"];
}

interface MappingSuggestion {
    supplierAttribute: string;
    retailerAttribute: string;
    confidence: number;
    source: string;
}

interface SnackbarState {
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
}
// endregion

const ConditionalCheckbox = (props: ConditionalCheckboxProps) => {
    const {validationRules} = props;

    return (
        <CatalogCheckbox
            checked={isStrictlyRequired(validationRules)}
        />
    )
}

export const isConditionallyRequired = (validationRules: RetailerAttribute["ValidationRules"] | undefined): boolean => {
    if (validationRules) {
        if(validationRules.find((rule) => {
            return rule.Type === "required";
        })) return true;
    }

    return false;
}

export const isStrictlyRequired = (validationRules: RetailerAttribute["ValidationRules"] | undefined): boolean => {
    if (validationRules) {
        if(validationRules.find((rule) => {
            return rule.Type === "required" && rule.Conditions === undefined;
        })) return true;
    }

    return false;
}

export default function AttributeMapping(props: AttributeMappingProps) {
    const {allowBack, allowNext, backStep, nextStep, activeStep, steps, setSteps} = props;
    const {profile, setProfile, formattedProfile, profileType} = props;
    const {fileParsedFields, sampleData} = props;
    const {retailerAttributes, isMarketplace} = props;
    const {refreshView} = props;

    const [parsedFields, setParsedFields] = useState<ParsedField[]>(fileParsedFields);


    const searchSx = {
        height: inputHeight,
        "& .MuiOutlinedInput-notchedOutline": { borderRadius: "6px" }
    };

    const [unmappedSupplierAttributes, setUnmappedSupplierAttributes] = useState<ParsedField[]>(parsedFields);
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [previousSupplierFields, setPreviousSupplierFields] = useState<ParsedField[]>(parsedFields);
    const [previousRetailerAttributes, setPreviousRetailerAttributes] = useState<RetailerAttribute[]>(retailerAttributes);

    const [currentView, setCurrentView] = useState<string>("all"); // all || retailer || supplier

    const [advancedAttributeMapperOpen, setAdvancedAttributeMapperOpen] = useState<boolean>(false);
    const [columnRuleEditorOpen, setColumnRuleEditorOpen] = useState<boolean>(false);
    const [selectedColumnRule, setSelectedColumnRule] = useState<string | null>(null);
    const [selectedColumnCalculated, setSelectedColumnCalculated] = useState<boolean>(false);
    const [groupedRules, setGroupedRules] = useState<Record<string, AttributeMappingRule[]>>({});

    const [addCalculatedColumnOpen, setAddCalculatedColumnOpen] = useState<boolean>(false);
    const [newCalculatedColumnName, setNewCalculatedColumnName] = useState<string>("");

    // AI Mapping states
    const [aiMappingInProgress, setAiMappingInProgress] = useState<boolean>(false);
    const [mappingReviewDialogOpen, setMappingReviewDialogOpen] = useState<boolean>(false);
    const [mappingSuggestions, setMappingSuggestions] = useState<MappingSuggestion[]>([]);
    const [processingStep, setProcessingStep] = useState<number | null>(null);
    const [applyingMappings, setApplyingMappings] = useState<boolean>(false);
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });

    //region Pagination Logic
    const [page, setPage] = React.useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = React.useState<number>(20);
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    //endregion

    //region Helper Functions
    const getAllUnmappedRetailerAttributes = useCallback((retailerAttributes: RetailerAttribute[], profile: any) => {
        const calculatedColumns = getAllCalculatedColumns();
        const unmappedAttributes = retailerAttributes.filter((attribute) => !profile.AttributeMappings[attribute.Name]);
        return [...calculatedColumns, ...unmappedAttributes];

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile.AttributeMappingRules]);

    const getAllUnmappedSupplierAttributes = useCallback((parsedFields: ParsedField[], profile: any) => {
        return parsedFields
            .filter((field) => !Object.values(profile.AttributeMappings).includes(field.field))
            .filter((field: any) => profile.AttributeMappingRules.find((rule: AttributeMappingRule) => rule.TargetField === field.field) === undefined);
    }, []);

    const getAllCalculatedColumns = (): RetailerAttribute[] => {
        return profile.AttributeMappingRules
            .filter((rule) => {
                return (!retailerAttributes.find((attribute) => attribute.Name === rule.TargetField))
            })
            .map((rule) => (
                {
                    IsCalculatedColumn: true,
                    Name: rule.TargetField as string,
                    FriendlyName: rule.TargetField as string,
                    Type: "text",
                    Description: "Calculated column.",
                    Options: ["string"],
                    Group: "Base",
                    System: false,
                    Tags: "",
                    Hidden: false,
                    HiddenFromPartner: false,
                    DestinationId: "string",
                    CustomAttributes: "string",
                    SyncStatus: "Not Synced",
                    Editor: "",
                    ValidationRules: [],
                    LastModified: "2024-09-20T14:56:45.9101699+00:00"
                }
            ));
    }

    const search = useCallback((term?: string) => {
        let operatingAttributes: any[] = currentView === "supplier" ? parsedFields : retailerAttributes;

        if (!term) {
            if (currentView === "supplier") {
                setPreviousSupplierFields(getAllUnmappedSupplierAttributes(parsedFields, profile));
            } else if (currentView === "retailer") {
                setPreviousRetailerAttributes(getAllUnmappedRetailerAttributes(retailerAttributes, profile));
            } else {
                setPreviousRetailerAttributes([...getAllCalculatedColumns(), ...retailerAttributes]);
            }
            return;
        }

        const termLower = term.toLowerCase();
        const matches = operatingAttributes.filter((attribute) => {
            if (currentView === "supplier") {
                return attribute.field.toLowerCase().includes(termLower);
            }
            return (
                attribute.Name.toLowerCase().includes(termLower) ||
                attribute.FriendlyName.toLowerCase().includes(termLower) ||
                (attribute.Description?.toLowerCase().includes(termLower) ?? false)
            );
        });

        if (currentView === "supplier") {
            setPreviousSupplierFields(matches);
        } else {
            setPreviousRetailerAttributes(matches);
        }

        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentView, parsedFields, retailerAttributes, profile]);

    const fuse = useMemo(() => {
        const fuseOpts = {
            includeScore: true,
            findAllMatches: false,
            ignoreLocation: true,
            threshold: 0.3,  // Aligned with the score check below
            keys: ['Name', 'FriendlyName']  // Search in both Name and FriendlyName
        };

        // Create searchable objects with both Name and FriendlyName
        const searchableAttributes = retailerAttributes.map(attribute => ({
            Name: attribute.Name,
            FriendlyName: attribute.FriendlyName
        }));

        return new Fuse(searchableAttributes, fuseOpts);
    }, [retailerAttributes]);

    const autoMatch = useCallback(async () => {
        setAiMappingInProgress(true);
        setMappingReviewDialogOpen(true);
        setProcessingStep(0);

        const suggestions: MappingSuggestion[] = [];
        const fuzzyMappings: Record<string, string> = {};
        const unmappedFields: string[] = [];
        const alreadyMapped = new Set(Object.values(profile.AttributeMappings).filter(v => v));
        const alreadyMappedRetailerAttrs = new Set(Object.keys(profile.AttributeMappings).filter(k => profile.AttributeMappings[k]));

        // Phase 1: Fuzzy matching
        parsedFields.forEach((field) => {
            if (!alreadyMapped.has(field.field)) {
                const result: any = fuse.search(field.field);
                if (result.length > 0 && result[0].score < 0.3) {
                    const matchedAttribute = result[0].item.Name;
                    // Only suggest if retailer attribute isn't already mapped
                    if (!alreadyMappedRetailerAttrs.has(matchedAttribute)) {
                        fuzzyMappings[matchedAttribute] = field.field;
                        suggestions.push({
                            supplierAttribute: field.field,
                            retailerAttribute: matchedAttribute,
                            confidence: 1 - result[0].score,
                            source: 'Fuzzy'
                        });
                    } else {
                        // Retailer attribute already mapped, treat as unmapped
                        unmappedFields.push(field.field);
                    }
                } else {
                    // No good fuzzy match
                    unmappedFields.push(field.field);
                }
            }
        });


        setProcessingStep(1);

        // Phase 2: AI mapping for unmapped fields (if enabled and there are unmapped fields)
        if (AI_MAPPING_ENABLED() && unmappedFields.length > 0) {
            try {
                const aiResponse: any = await aiMapAttributes(
                    unmappedFields,
                    retailerAttributes.map(attr => attr.Name)
                );

                if (aiResponse.mappings) {
                    Object.entries(aiResponse.mappings as Record<string, any>).forEach(([supplierAttr, mapping]) => {
                        // Only add AI suggestion if the retailer attribute isn't already mapped
                        if (!alreadyMappedRetailerAttrs.has(mapping.suggested) &&
                            !fuzzyMappings[mapping.suggested]) {
                            suggestions.push({
                                supplierAttribute: supplierAttr,
                                retailerAttribute: mapping.suggested,
                                confidence: mapping.confidence,
                                source: mapping.source
                            });
                        }
                    });
                }

                if (aiResponse.errors && aiResponse.errors.length > 0) {
                    console.warn('AI mapping errors:', aiResponse.errors);
                }
            } catch (error) {
                console.error('AI mapping failed:', error);
                // Continue with fuzzy results only
            }
        }

        setProcessingStep(2);
        setMappingSuggestions(suggestions);
        setAiMappingInProgress(false);
    }, [parsedFields, profile.AttributeMappings, fuse, retailerAttributes]);

    const handleApplyMappings = useCallback((acceptedMappings: MappingSuggestion[]) => {
        setApplyingMappings(true);

        // Simulate async operation for applying mappings
        setTimeout(() => {
            const newMappings: Record<string, string> = {};
            acceptedMappings.forEach(mapping => {
                newMappings[mapping.retailerAttribute] = mapping.supplierAttribute;
            });

            setProfile(prev => ({
                ...prev,
                AttributeMappings: {
                    ...prev.AttributeMappings,
                    ...newMappings
                }
            }));

            setApplyingMappings(false);
            setMappingReviewDialogOpen(false);
            setMappingSuggestions([]);

            // Show success notification with unmapped count
            const mappingCount = acceptedMappings.length;
            const totalFields = parsedFields.length;
            const mappedFields = Object.values(profile.AttributeMappings).filter(v => v).length + mappingCount;
            const unmappedCount = totalFields - mappedFields;

            let message = `Successfully applied ${mappingCount} mapping${mappingCount !== 1 ? 's' : ''}`;
            if (unmappedCount > 0) {
                message += `. ${unmappedCount} field${unmappedCount !== 1 ? 's' : ''} remain unmapped.`;
            }

            setSnackbar({
                open: true,
                message,
                severity: 'success'
            });
        }, 500);
    }, [setProfile, parsedFields, profile.AttributeMappings]);

    const onNext = () => {
        let unmappedSupplierAttributes: string[] = [];
        let unmappedRetailerAttributes: string[] = [];

        // Check if any supplier attributes are unmapped
        // parsedFields.forEach((field) => {
        //     if (Object.values(profile.AttributeMappings).indexOf(field.field) === -1)
        //         unmappedSupplierAttributes.push(field.field);
        // });

        // Check if any required retailer attributes are unmapped
        retailerAttributes.forEach((attribute) => {
            if (attribute.ValidationRules) {
                if (attribute.ValidationRules.find((rule) => rule.Type === "required")) {
                    if (!profile.AttributeMappings[attribute.Name]) {
                        unmappedRetailerAttributes.push(attribute.Name);
                    }
                }
            }
        });

        if (unmappedSupplierAttributes.length > 0 || unmappedRetailerAttributes.length > 0) {
            let errors: string[] = [];

            if (unmappedSupplierAttributes.length > 0) {
                let msg1 = `Please map the following supplier attributes: ${unmappedSupplierAttributes.join(", ")}.`;
                errors.push(msg1);
            }

            if (unmappedRetailerAttributes.length > 0) {
                let msg2 = `The following retailer attributes are required: ${unmappedRetailerAttributes.join(", ")}.`;
                errors.push(msg2);
            }

            setSteps((prevState) => {
                let newState = cloneDeep(prevState);
                newState[activeStep].status = "warning";
                newState[activeStep].errors = errors;
                return newState;
            });
        } else {
            setSteps((prevState) => {
                let newState = cloneDeep(prevState);
                newState[activeStep].status = "success";
                newState[activeStep].errors = undefined;
                return newState;
            });
        }
    }
    //endregion

    //region Row Rendering
    const displayRetailerAttributes = React.useMemo(() => {
        return previousRetailerAttributes
            .sort((a, b) => {
                // Calculated columns should be shown first
                if(a.IsCalculatedColumn && !b.IsCalculatedColumn) return -1;
                if(!a.IsCalculatedColumn && b.IsCalculatedColumn) return 1;

                if (isConditionallyRequired(a.ValidationRules) && !isConditionallyRequired(b.ValidationRules)) return -1;
                if (!isConditionallyRequired(a.ValidationRules) && isConditionallyRequired(b.ValidationRules)) return 1;

                if (profile.AttributeMappings[a.Name] && !profile.AttributeMappings[b.Name]) return -1;
                if (!profile.AttributeMappings[a.Name] && profile.AttributeMappings[b.Name]) return 1;

                return 0;
            })
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((attribute, index) => {
                let mappedSupplierAttribute = profile.AttributeMappings[attribute.Name];
                let mappedRule = profile.AttributeMappingRules.find((rule) => rule.TargetField === attribute.Name);
                let mappingBtnDisabled = ((mappedSupplierAttribute && !(mappedSupplierAttribute && mappedRule)) && !attribute.IsCalculatedColumn);

                return (
                    <TableRow key={index} sx={tableRowSx}>
                        {(isMarketplace && SUPPLIER_SYNDICATION_ENABLED) && (
                            <TableCell style={{padding: "5px", border: "none"}}>
                                <Button
                                    disabled={Boolean(mappingBtnDisabled)}
                                    variant={groupedRules[attribute.Name] ? "contained" : "outlined"}
                                    style={{
                                        width: "40px",
                                        minWidth: "40px",
                                        height: "40px",
                                        backgroundColor: mappingBtnDisabled
                                            ? "white"
                                            : groupedRules[attribute.Name]
                                                ? "#006BFF"
                                                : "white",
                                        color: mappingBtnDisabled
                                            ? "#CECECEFF"
                                            : groupedRules[attribute.Name]
                                                ? "white"
                                                : "black",
                                        borderColor: mappingBtnDisabled
                                            ? "#CECECEFF"
                                            : groupedRules[attribute.Name]
                                                ? "#006BFF"
                                                : "black"
                                    }}
                                    onClick={() => {
                                        setColumnRuleEditorOpen(true);
                                        setSelectedColumnRule(attribute.Name);
                                        setSelectedColumnCalculated(Boolean(attribute.IsCalculatedColumn));
                                    }}
                                >
                                    <Ballot />
                                </Button>
                            </TableCell>
                        )}
                        <MappingCell
                                   style={{padding: "12px 0px 12px 24px"}}>
                            <Grid container justifyContent={"space-between"} alignItems={"center"}
                                  style={{display: "flex", gap: pageContentGap}}>
                                {groupedRules[attribute.Name] ? (
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        backgroundColor: "lightblue",
                                        opacity: 0.8,
                                        border: "#006BFF solid 1px",
                                        borderRadius: "4px",
                                        flex: 1,
                                        height: "40px"
                                    }}>
                                        <Typography variant={"subtitle2"} style={{color: "#006BFF", fontWeight: "bold"}}>
                                            {groupedRules[attribute.Name].length} Rule(s)
                                        </Typography>
                                    </div>
                                ) : (
                                    <EnhancedSelect
                                        placeholder={"Supplier Attribute"}
                                        isClearable
                                        options={parsedFields.map((field, index) => {
                                            return {
                                                label: field.field,
                                                value: field.field
                                            }
                                        })}
                                        value={{
                                            label: mappedSupplierAttribute,
                                            value: mappedSupplierAttribute
                                        }}
                                        onChange={(e: any) => {
                                            if (e == null) {
                                                const copiedProfile = cloneDeep(profile);
                                                delete copiedProfile.AttributeMappings[attribute.Name];
                                                setProfile(copiedProfile);
                                                return;
                                            }

                                            setProfile({
                                                ...profile,
                                                AttributeMappings: {
                                                    ...profile.AttributeMappings,
                                                    [attribute.Name]: e.value
                                                }
                                            });
                                        }}
                                    />
                                )}

                                <MappingArrow/>
                            </Grid>
                        </MappingCell>
                        <MappingCell
                                   style={{padding: "12px 12px 12px 12px"}}>
                            {attribute.IsCalculatedColumn ? (
                                <div style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "lightblue",
                                    opacity: 0.8,
                                    border: "#006BFF solid 1px",
                                    borderRadius: "4px",
                                    flex: 1,
                                    height: "40px"
                                }}>
                                    <Typography variant={"subtitle2"} style={{color: "#006BFF", fontWeight: "bold"}}>
                                        calc: {attribute.FriendlyName}
                                    </Typography>
                                </div>
                            ) : (
                                <Typography>
                                    {attribute.FriendlyName}
                                </Typography>
                            )}
                        </MappingCell>
                        <MappingCell
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <ConditionalCheckbox
                                validationRules={attribute.ValidationRules}/>
                        </MappingCell>
                        <MappingCell
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <Typography>
                                {attribute.Type ?? ""}
                            </Typography>
                        </MappingCell>
                        <MappingCell
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <Typography>
                                {attribute.Description ?? ""}
                            </Typography>
                        </MappingCell>
                    </TableRow>
                );
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshView, previousRetailerAttributes, retailerAttributes, profile.AttributeMappings, currentView, page, rowsPerPage, parsedFields]);

    const displaySupplierAttributes = React.useMemo(() => {
        return previousSupplierFields
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((field, index) => {
                let foundMappedAttribute: any = retailerAttributes.find((attribute) => {
                    return profile.AttributeMappings[attribute.Name] === field.field;
                });
                if (!foundMappedAttribute) foundMappedAttribute = {
                    Name: "",
                    FriendlyName: "",
                    ValidationRules: undefined,
                    Type: "",
                    Description: ""
                }

                return (
                    <TableRow key={index} sx={tableRowSx}>
                        <MappingCell
                                   style={{padding: "12px 0px 12px 24px"}}>
                            <Grid container justifyContent={"space-between"} alignItems={"center"}
                                  style={{display: "flex", gap: pageContentGap, flexWrap: "nowrap"}}>
                                <div style={{display: "inline-flex", alignItems: "center", gap: pageContentGap}}>
                                    <Typography>
                                        {field.field}
                                    </Typography>
                                    <MappingArrow/>
                                </div>
                            </Grid>
                        </MappingCell>
                        <MappingCell
                                   style={{padding: "12px 12px 12px 12px"}}>
                            {groupedRules[(field as any).Name] ? (
                                <div style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "lightblue",
                                    opacity: 0.8,
                                    border: "#006BFF solid 1px",
                                    borderRadius: "4px",
                                    flex: 1,
                                    height: "40px"
                                }}>
                                    <Typography variant={"subtitle2"} style={{color: "#006BFF", fontWeight: "bold"}}>
                                        {groupedRules[(field as any).Name].length} Rule(s)
                                    </Typography>
                                </div>
                            ) : (
                                <EnhancedSelect
                                    placeholder={"Retailer Attribute"}
                                    isClearable
                                    options={retailerAttributes.map((attribute, index) => {
                                        return {
                                            label: attribute.FriendlyName,
                                            value: attribute.Name
                                        }
                                    })}
                                    value={{
                                        label: foundMappedAttribute?.FriendlyName ?? "",
                                        value: foundMappedAttribute?.Name ?? ""
                                    }}
                                    onChange={(e: any) => {
                                        let key = "";
                                        let value = "";

                                        if (e == null) {
                                            const copiedProfile = cloneDeep(profile);
                                            delete copiedProfile.AttributeMappings[foundMappedAttribute.Name];
                                            setProfile(copiedProfile);
                                            return;
                                        } else if (profile.AttributeMappings[e.value] != null) {
                                            key = foundMappedAttribute.Name ?? "";
                                            value = field.field;
                                        } else {
                                            key = e.value ?? "";
                                            value = field.field;
                                        }

                                        // If retailer attribute is already mapped to another supplier attribute, overwrite the previous mapping
                                        if(key === "") key = e.value;

                                        let prevValue = profile.AttributeMappings[foundMappedAttribute.Name];
                                        if (prevValue != null) {
                                            let prevKey = Object.keys(profile.AttributeMappings).find(key => profile.AttributeMappings[key] === field.field);
                                            if (prevKey != null) {
                                                setProfile({
                                                    ...profile,
                                                    AttributeMappings: {
                                                        ...profile.AttributeMappings,
                                                        [prevKey]: null
                                                    }
                                                });
                                            }
                                        } else {
                                            setProfile({
                                                ...profile,
                                                AttributeMappings: {
                                                    ...profile.AttributeMappings,
                                                    [key]: value
                                                }
                                            });
                                        }
                                    }}
                                />
                            )}
                        </MappingCell>
                        <MappingCell
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <ConditionalCheckbox
                                validationRules={foundMappedAttribute.ValidationRules}/>
                        </MappingCell>
                        <MappingCell
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <Typography>
                                {foundMappedAttribute.Type ?? ""}
                            </Typography>
                        </MappingCell>
                        <MappingCell
                                   style={{padding: "12px 12px 12px 12px"}}>
                            <Typography>
                                {foundMappedAttribute.Description ?? ""}
                            </Typography>
                        </MappingCell>
                    </TableRow>
                );
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshView, previousSupplierFields, retailerAttributes, profile.AttributeMappings, currentView, page, rowsPerPage, parsedFields]);
    //endregion

    //region Lifecycle
    (useDebouncedEffect as any)(() => {
        search(searchTerm);
    }, [searchTerm], 500);

    useEffect(() => {
        setUnmappedSupplierAttributes(parsedFields.filter((field) => {
            const mapping = !Object.values(profile.AttributeMappings).includes(field.field);
            const mappingRule = profile.AttributeMappingRules.find((rule) => rule.TargetField === field.field) === undefined;
            return mapping && mappingRule;
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile.AttributeMappings, refreshView]);

    useEffect(() => {
        // Update parsed fields to select from
        const newParsedFields = [
            ...fileParsedFields,
            ...profile.AttributeMappingRules.map((rule) => ({
                field: rule.TargetField as string,
                content: ""
            }))
        ];
        setParsedFields(newParsedFields)

        // Update grouped rules
        const newGroupedRules: Record<string, AttributeMappingRule[]> = {};
        profile.AttributeMappingRules.forEach((rule) => {
            const tf = rule.TargetField as string;
            if(!newGroupedRules[tf]) newGroupedRules[tf] = [];
            newGroupedRules[tf].push(rule);
        });
        setGroupedRules(newGroupedRules);

        const renderedCalculatedColumns = getAllCalculatedColumns();

        // Update displayed attributes to include calculated columns and ensure they are shown first
        if(currentView === "retailer") {
            setPreviousRetailerAttributes((prev) => [
                ...renderedCalculatedColumns.filter((column) => {
                    return !prev.find((attribute) => attribute.Name === column.Name)
                }),
                ...prev
            ]);
        }
        else {
            setPreviousRetailerAttributes([
                ...renderedCalculatedColumns,
                ...retailerAttributes
            ]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile.AttributeMappingRules, refreshView]);

    useEffect(() => {
        onNext();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    //endregion

    return (
        <Grid container style={{gap: pageContentGap}}>
            {(steps[activeStep].status === "error" || steps[activeStep].errors) && (
                <>
                    {(steps[activeStep].errors ?? []).map((error: string, index: number) => {
                        return (
                            <Card key={index} id="cueMessage"
                                  style={{backgroundColor: '#ee3224', color: 'white', width: "100%"}}>
                                <CardContent style={{display: 'flex', alignItems: 'center'}}>
                                    <ErrorIcon style={{marginRight: '1rem'}}/>
                                    <Typography style={{
                                        textAlign: "left",
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        maxHeight: "100px"
                                    }}>
                                        {error}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )
                    })}
                </>
            )}
            <Paper style={{width: "100%", padding: "24px", borderRadius: "5px"}}>
                <Grid container style={{gap: pageContentGap, flexDirection: "column", alignItems: "flex-start", width: "100%"}}>
                    <Grid container justifyContent={"space-between"} style={{width: "100%", gap: "5px", flexDirection: "column", alignItems: "flex-start"}}>
                        <SectionTitle style={{marginBottom: "12px"}}>
                            Attribute Mapping
                        </SectionTitle>

                        <Grid item style={{gap: "6px", display: "flex"}}>
                                <Button
                                    variant={"outlined"}
                                    onClick={() => setAdvancedAttributeMapperOpen(true)}
                                >
                                    Advanced Mapping Tools
                                </Button>
                            {(isMarketplace && SUPPLIER_SYNDICATION_ENABLED) && (
                                <Button
                                    variant={"outlined"}
                                    onClick={() => {
                                        setAddCalculatedColumnOpen(true)
                                    }}
                                >
                                    Add Calculated Column
                                </Button>
                            )}

                            <Button variant={currentView === "retailer" ? "contained" : "outlined"} color="primary"
                                onClick={() => {
                                    setSearchTerm("");
                                    setPreviousRetailerAttributes(getAllUnmappedRetailerAttributes(retailerAttributes, profile));
                                    setCurrentView("retailer");
                                    setPage(0);
                                }}
                            >
                                Unmapped Retailer Attributes
                            </Button>

                            <Button variant={currentView === "supplier" ? "contained" : "outlined"} color="primary"
                                onClick={() => {
                                    setSearchTerm("");
                                    setPreviousSupplierFields(getAllUnmappedSupplierAttributes(parsedFields, profile));
                                    setCurrentView("supplier");
                                    setPage(0)
                                }}
                            >
                                Your Unmapped Attributes
                            </Button>

                            <Button variant={currentView === "all" ? "contained" : "outlined"} color="primary"
                                onClick={() => {
                                    setSearchTerm("");
                                    setPreviousRetailerAttributes([
                                        ...getAllCalculatedColumns(),
                                        ...retailerAttributes
                                    ]);
                                    setCurrentView("all");
                                }}
                            >
                                All Attributes
                            </Button>
                        </Grid>
                    </Grid>

                    <Grid container style={{flexDirection: "column", width: "100%"}}>
                        {/*Check if there are any unmapped supplier attributes*/}
                        {unmappedSupplierAttributes.length > 0 && (
                            <Notifier color="#FFB600" key={0}
                                      style={{marginBottom: '5px', flex: 1, position: "relative"}}
                                      barstyles={{
                                          height: "100%", position: "absolute"
                                      }}
                                      messagestyles={{
                                          marginLeft: "5px"
                                      }}
                            >
                                {props.isRetailer ? "Your unmapped attributes:" : "Unmapped supplier attributes:"} <span> </span>
                                {unmappedSupplierAttributes
                                    .map((field, index) => {
                                        return (
                                            <span
                                                key={index}>{field.field}{index === unmappedSupplierAttributes.length - 1 ? "" : ", "}</span>
                                        )
                                    })}
                            </Notifier>
                        )}
                    </Grid>

                    <Grid container style={{width: "100%"}}>
                        <OutlinedInput
                            placeholder={"Search"}
                            value={searchTerm}
                            style={{width: "100%"}}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                // search(e.target.value);
                            }}
                            sx={searchSx}
                            startAdornment={
                                <InputAdornment style={{cursor: "pointer"}} position="start"
                                                onClick={() => search()}>
                                    <SearcherIcon/>
                                </InputAdornment>
                            }
                            endAdornment={
                                <>
                                    {searchTerm && (
                                        <InputAdornment style={{cursor: "pointer"}} position="end"
                                                        onClick={() => {
                                                            setSearchTerm("");
                                                            search();
                                                        }}>
                                            <Clear style={{width: "20px", height: "20px", opacity: "0.8"}}/>
                                        </InputAdornment>
                                    )}
                                </>
                            }
                        />
                    </Grid>

                    <Grid container>
                        <Table style={{tableLayout: "fixed"}}>
                            <TableHead>
                                <TableRow>
                                    {(isMarketplace && SUPPLIER_SYNDICATION_ENABLED && currentView !== "supplier") && (
                                        <TableCell style={{border: "none", width: "40px", padding: 0}}>
                                            {/*PLACEHOLDER FOR RULES BUTTON*/}
                                        </TableCell>
                                    )}

                                    <TableCell style={{border: "none"}}>
                                        <Grid container alignItems={"center"} style={{gap: pageContentGap}}>
                                            <MappingHeader>
                                                Your Attribute
                                            </MappingHeader>

                                            <Button variant="outlined" color="primary"
                                                style={{
                                                    height: "32px",
                                                    lineHeight: 1,
                                                    minWidth: "120px"
                                                }}
                                                onClick={() => autoMatch()}
                                                disabled={aiMappingInProgress}
                                                startIcon={aiMappingInProgress ? <CircularProgress size={16} /> : null}
                                            >
                                                {aiMappingInProgress ? "Matching..." : (AI_MAPPING_ENABLED() ? "Auto Match (AI)" : "Auto Match")}
                                            </Button>
                                        </Grid>
                                    </TableCell>

                                    <TableCell style={{border: "none", padding: "12px 12px 12px 12px"}}>
                                        <Grid container alignItems={"center"} style={{gap: pageContentGap}}>
                                            <MappingHeader>
                                                Retailer Attribute
                                            </MappingHeader>
                                        </Grid>
                                    </TableCell>

                                    <TableCell style={{border: "none", padding: "12px 12px 12px 12px"}}>
                                        <Grid container alignItems={"center"} style={{gap: pageContentGap}}>
                                            <MappingHeader>
                                                Required
                                            </MappingHeader>
                                        </Grid>
                                    </TableCell>

                                    <TableCell style={{border: "none", padding: "12px 12px 12px 12px"}}>
                                        <Grid container alignItems={"center"} style={{gap: pageContentGap}}>
                                            <MappingHeader>
                                                Type
                                            </MappingHeader>
                                        </Grid>
                                    </TableCell>

                                    <TableCell style={{border: "none", padding: "12px 12px 12px 12px"}}>
                                        <Grid container alignItems={"center"} style={{gap: pageContentGap}}>
                                            <MappingHeader>
                                                Description
                                            </MappingHeader>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {currentView === "supplier" ? displaySupplierAttributes : displayRetailerAttributes}
                            </TableBody>
                        </Table>
                        <TablePagination
                            style={{float: "right", width: "100%"}}
                            rowsPerPageOptions={[20, 50, 100]}
                            component="div"
                            count={currentView === "supplier" ? previousSupplierFields.length : previousRetailerAttributes.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Grid>
                </Grid>
            </Paper>
            <NavigationButtons
                allowBack={allowBack}
                allowNext={allowNext}
                backStep={backStep}
                nextStep={nextStep}
                onNext={onNext}
                activeStep={activeStep}
                steps={steps}

                formattedProfile={formattedProfile}
                profileType={profileType}
            />
            {advancedAttributeMapperOpen && (
                <AttributeMapper
                    attributes={retailerAttributes}
                    parsedFields={parsedFields}
                    open={advancedAttributeMapperOpen}
                    setOpen={setAdvancedAttributeMapperOpen}
                    isRetailer={props.isRetailer}
                    profile={profile}
                    setProfile={setProfile}
                />
            )}
            {columnRuleEditorOpen && selectedColumnRule != null && (
                <ColumnRuleEditor
                    open={columnRuleEditorOpen}
                    setOpen={setColumnRuleEditorOpen}
                    attributes={retailerAttributes}
                    sampleData={sampleData}
                    parsedFields={parsedFields}
                    profile={profile}
                    setProfile={setProfile}
                    column={selectedColumnRule}
                    isCalculatedColumn={selectedColumnCalculated}
                    groupedRule={groupedRules[selectedColumnRule]}
                />
            )}
            {addCalculatedColumnOpen && (
                <Dialog
                    open={addCalculatedColumnOpen}
                    onClose={() => {
                        setAddCalculatedColumnOpen(false)
                        setNewCalculatedColumnName("")
                    }}
                    maxWidth={"sm"}
                >
                    <DialogTitle style={{padding: "8px"}}>
                        Add Calculated Column
                    </DialogTitle>

                    <DialogContent style={{paddingTop: 8, paddingBottom: 8, overflow: "hidden"}}>
                        <OutlinedInput
                            style={{height: "35px"}}
                            placeholder={"New Column Name"}
                            value={newCalculatedColumnName}
                            onChange={(e) => setNewCalculatedColumnName(e.target.value)}
                        />
                    </DialogContent>

                    <DialogActions>
                        <Button
                            variant={"outlined"}
                            onClick={() => {
                                setAddCalculatedColumnOpen(false)
                                setNewCalculatedColumnName("")
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant={"outlined"}
                            onClick={() => {
                                setProfile((prevProfile) => {
                                    return {
                                        ...prevProfile,
                                        AttributeMappingRules: [
                                            ...prevProfile.AttributeMappingRules,
                                            {
                                                TargetField: newCalculatedColumnName,
                                                SourceField: "",
                                                Condition: ""
                                            }
                                        ]
                                    }
                                })
                                setAddCalculatedColumnOpen(false)
                            }}
                        >
                            Add
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {mappingReviewDialogOpen && (
                <MappingReviewDialog
                    open={mappingReviewDialogOpen}
                    onClose={() => {
                        setMappingReviewDialogOpen(false);
                        setMappingSuggestions([]);
                        setProcessingStep(null);
                    }}
                    mappingSuggestions={mappingSuggestions as any}
                    onApply={handleApplyMappings as any}
                    isProcessing={aiMappingInProgress}
                    processingStep={processingStep}
                    mappingType="attributes"
                    isApplying={applyingMappings}
                />
            )}
            <EnhancedSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </Grid>
    );
}
